import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Form, Button, Spinner } from '../../../components/ui';
import { toast } from '../../../components/ui/notify';

import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../../../services/user/Settings.services";
import { getProfile, editProfile } from "../../../services/user/profile.services";
import {
  ensureWebPushRegistration,
  revokeCurrentWebPushToken,
} from "../../../services/webPush/firebaseWebPush";
import { resolveTimeZoneOption } from "../../../utils/timezone";
import { useTimezone } from "../../../context/TimezoneContext";
import TimeZoneDropdown from "../../../components/common/TimeZoneDropdown";

const DEFAULT_SETTINGS = {
  web: { is_configured: 0, is_active: 0 },
  email: { is_configured: 0, is_active: 0 },
  sms: { is_configured: 0, is_active: 0 },
  mobile: { is_configured: 0, is_active: 0 },
};

const CHANNELS = [
  {
    key: "web",
    label: "Web",
    description: "Receive browser push notifications on this device.",
    icon: "mdi:web",
  },
  {
    key: "mobile",
    label: "Mobile",
    description: "Push notifications on your iOS or Android app.",
    icon: "mdi:cellphone",
  },
  {
    key: "email",
    label: "Email",
    description: "Receive notification emails on your verified email address.",
    icon: "mdi:email-outline",
  },
];

function Notification() {
  const { timeZone, setTimeZone } = useTimezone();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState(DEFAULT_SETTINGS);
  const [selectedTimeZone, setSelectedTimeZone] = useState(() =>
    resolveTimeZoneOption(timeZone)
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const plivoEnabled = settings?.plivo_enabled !== false;

  const fetchNotificationSettings = async () => {
    try {
      setLoading(true);
      const [notificationResponse, profileResponse] = await Promise.all([
        getNotificationSettings(),
        getProfile(),
      ]);
      if (notificationResponse?.success && notificationResponse?.data) {
        const nextSettings = {
          ...DEFAULT_SETTINGS,
          ...notificationResponse.data,
        };

        // If browser permission is blocked, never show Web as active in UI.
        if (typeof Notification !== "undefined" && Notification.permission === "denied") {
          nextSettings.web = {
            ...(nextSettings.web || DEFAULT_SETTINGS.web),
            is_active: 0,
          };
        }

        setSettings(nextSettings);
        setInitialSettings(nextSettings);
      } else {
        toast.error(notificationResponse?.message || "Failed to load notification settings");
      }

      if (profileResponse?.success) {
        const resolvedTimeZone = resolveTimeZoneOption(profileResponse.data?.time_zone);
        setSelectedTimeZone(resolvedTimeZone);
        setTimeZone(resolvedTimeZone);
      }
    } catch (error) {
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const handleToggle = (channelKey) => {
    if (channelKey === "web") {
      const currentValue = Number(settings.web?.is_active) === 1;
      const nextValue = currentValue ? 0 : 1;

      if (nextValue === 1 && typeof Notification !== "undefined") {
        if (Notification.permission === "denied") {
          toast.error(
            "Enable browser notifications in site settings."
          );
          return;
        }
      }
    }

    setSettings((prev) => ({
      ...prev,
      [channelKey]: {
        ...prev[channelKey],
        is_active: prev[channelKey]?.is_active ? 0 : 1,
      },
    }));
  };

  const handleSave = async () => {
    const saveStartedAt = Date.now();
    try {
      setSaving(true);
      const wantsWebEnabled = Number(settings.web?.is_active) === 1;
      const wasWebEnabled = Number(initialSettings.web?.is_active) === 1;
      const webChanged = wantsWebEnabled !== wasWebEnabled;
      let effectiveWebEnabled = wantsWebEnabled;
      let webRegistrationWarning = "";

      if (wantsWebEnabled && webChanged) {
        const registration = await ensureWebPushRegistration({ requestPermission: true });
        if (registration.status === "denied" || registration.status === "not_granted") {
          toast.error("Enable browser notifications in site settings.");
          effectiveWebEnabled = false;
          setSettings((prev) => ({
            ...prev,
            web: { ...(prev.web || DEFAULT_SETTINGS.web), is_active: 0 },
          }));
        }

        if (registration.status === "unsupported") {
          toast.error("This browser does not support web push notifications.");
          effectiveWebEnabled = false;
          setSettings((prev) => ({
            ...prev,
            web: { ...(prev.web || DEFAULT_SETTINGS.web), is_active: 0 },
          }));
        }

        if (registration.status !== "registered") {
          webRegistrationWarning = " (web push not available on this device)";
          console.warn("[NotificationSettings] Web push registration skipped:", registration);
        }
      } else if (!wantsWebEnabled && webChanged) {
        await revokeCurrentWebPushToken();
      }

      const response = await updateNotificationSettings({
        web_enabled: effectiveWebEnabled ? 1 : 0,
        mobile_enabled: Number(settings.mobile?.is_active) === 1 ? 1 : 0,
        email_enabled: settings.email.is_active ? 1 : 0,
        sms_enabled: plivoEnabled && settings.sms.is_active ? 1 : 0,
      });

      const profileUpdateResponse = await editProfile({
        time_zone: selectedTimeZone,
      });

      if (response?.success && response?.data && profileUpdateResponse?.success) {
        const remainingDelay = Math.max(0, 2000 - (Date.now() - saveStartedAt));
        if (remainingDelay > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingDelay));
        }

        const nextSettings = {
          ...DEFAULT_SETTINGS,
          ...response.data,
        };
        setSettings(nextSettings);
        setInitialSettings(nextSettings);
        setTimeZone(selectedTimeZone);

        toast.success(`${response?.message || "Notification preferences saved"}${webRegistrationWarning}`);
      } else {
        toast.error(
          response?.message ||
            profileUpdateResponse?.message ||
            "Failed to update notification settings"
        );
      }
    } catch (error) {
      toast.error("Failed to update notification settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tw-text-center tw-p-6">
        <Spinner animation="border" variant="primary" />
        <p className="tw-mt-2 tw-mb-0">Loading notification settings...</p>
      </div>
    );
  }

  return (
    <div className="tw-p-0 notification-settings-container">
      <div className={"[&_h4]:tw-text-[18px] [&_h4]:tw-font-semibold [&_h4]:tw-text-[#333] [&_h4]:tw-mb-[8px] max-[768px]:tw-p-[16px] notification-channels"}>
        <h4>Notification Channels</h4>
        <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Choose how you want to receive notifications.</p>

        <div className={"[&_.timezone-dropdown-wrapper]:tw-w-[50%] [&_.timezone-dropdown-wrapper]:tw-max-w-full max-[768px]:[&_.timezone-dropdown-wrapper]:tw-w-full tw-overflow-visible tw-mb-6 notification-timezone-field"}>
          <Form.Group>
            <Form.Label className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">Time Zone</Form.Label>
            <TimeZoneDropdown
              value={selectedTimeZone}
              onChange={setSelectedTimeZone}
              disabled={saving}
            />
            <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block tw-mt-2">
              This timezone will be used for all date/time display across the application.
            </small>
          </Form.Group>
        </div>

        <div className="tw-mt-[16px] notification-toggle-group">
          {CHANNELS.map((channel) => {
            const channelState = settings[channel.key] || DEFAULT_SETTINGS[channel.key];
            const configured = Number(channelState.is_configured) === 1;
            const canToggle =
              (channel.key === "web" || configured) && (channel.key !== "sms" || plivoEnabled);

            return (
              <div
                className={`tw-flex tw-justify-between tw-items-center tw-p-[16px_0] tw-border-b-[1px] tw-border-solid tw-border-b-[#eee] [&.is-disabled]:tw-opacity-[0.55] [&.is-disabled_.toggle-switch]:tw-cursor-not-allowed [&.is-disabled_.toggle-switch]:tw-pointer-events-none [&:last-child]:tw-border-b-0 notification-toggle-item   ${!canToggle ? "is-disabled" : ""}`}
                key={channel.key}
              >
                <div className="tw-flex tw-items-start [flex:1] toggle-info">
                  <div className="tw-flex tw-items-center tw-justify-center tw-w-[40px] tw-h-[40px] tw-bg-[rgba(49,_82,_232,_0.1)] tw-rounded-[8px] tw-mr-[16px] tw-text-[#3152e8] max-[768px]:tw-w-[32px] max-[768px]:tw-h-[32px] max-[768px]:tw-mr-[12px] toggle-icon">
                    <Icon icon={channel.icon} width="20" height="20" />
                  </div>
                  <div className={"[&_h5]:tw-text-[16px] [&_h5]:tw-font-medium [&_h5]:tw-text-[#333] [&_h5]:tw-mb-[4px] [&_p]:tw-text-[14px] [&_p]:tw-text-[#666] [&_p]:tw-m-0 max-[768px]:[&_h5]:tw-text-[15px] max-[768px]:[&_p]:tw-text-[13px] toggle-details"}>
                    <h5>{channel.label}</h5>
                    <p>{channel.description}</p>
                  </div>
                </div>
                <div className="toggle-switch">
                  <Form.Check
                    type="switch"
                    id={`${channel.key}-switch`}
                    checked={configured && Number(channelState.is_active) === 1}
                    onChange={() => handleToggle(channel.key)}
                    disabled={!canToggle}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="tw-flex tw-justify-end tw-mt-6">
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="tw-me-2" />
                Saving...
              </>
            ) : (
              "Save Notification Settings"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Notification;
